import type { NorthStarSpec } from '../types';

export type NorthStarUnit = NorthStarSpec['unit'];

/** Big numeric value plus a small trailing unit suffix for the hero/drill. */
export function formatNorthStarValue(
  value: number,
  unit: NorthStarUnit,
): { value: string; suffix: string } {
  switch (unit) {
    case 'pct':
      return { value: String(value), suffix: '%' };
    case 'eur':
      return { value: value.toLocaleString('de-CH'), suffix: '€' };
    case 'min':
      return { value: String(value), suffix: 'min' };
    case 'x':
      return { value: String(value), suffix: '×' };
    case 'count':
      return { value: value.toLocaleString('de-CH'), suffix: '' };
    case 'score':
    default:
      // Composite scores are normalised to a 0–100 index.
      return { value: String(value), suffix: '/ 100' };
  }
}

/** Inline single-string variant (e.g. baseline copy). */
export function formatNorthStarValueInline(
  value: number,
  unit: NorthStarUnit,
): string {
  const { value: v, suffix } = formatNorthStarValue(value, unit);
  if (!suffix) {
    return v;
  }
  return suffix === '%' || suffix === '×' ? `${v}${suffix}` : `${v} ${suffix}`;
}

/** Format a component metric value using its own catalog unit ('%', '€', etc.). */
export function formatComponentValue(value: number, unit?: string): string {
  if (unit === '%') {
    return `${value}%`;
  }
  if (unit === '€') {
    return `${value.toLocaleString('de-CH')} €`;
  }
  if (unit === 'min' || unit === 'm') {
    return `${value} min`;
  }
  if (unit === 'x') {
    return `${value}×`;
  }
  if (unit === 'count') {
    return value.toLocaleString('de-CH');
  }
  return String(value);
}

/** Where a North Star plan item points on the dashboard. */
export type NorthStarNavTarget =
  | { kind: 'metric'; metricKey: string }
  | { kind: 'reviews' }
  | { kind: 'settings' }
  | { kind: 'overview' };

const DRILLABLE_METRIC_KEYS = new Set([
  'covers',
  'reservations',
  'noshow',
  'cancel',
  'turns',
  'occupancy',
  'seatTime',
  'sellitems',
  'revenue',
  'avgCheck',
  'revpash',
]);

/** Map a plan item (category + metricKey) to a dashboard destination. */
export function planItemNavTarget(item: {
  category: 'metric' | 'settings' | 'operations';
  metricKey?: string;
}): NorthStarNavTarget {
  if (item.category === 'settings') {
    return { kind: 'settings' };
  }
  if (item.metricKey === 'sentiment') {
    return { kind: 'reviews' };
  }
  if (item.metricKey && DRILLABLE_METRIC_KEYS.has(item.metricKey)) {
    return { kind: 'metric', metricKey: item.metricKey };
  }
  return { kind: 'overview' };
}

export function navTargetLabel(target: NorthStarNavTarget): string {
  switch (target.kind) {
    case 'metric':
      return 'Open metric';
    case 'reviews':
      return 'Open reviews';
    case 'settings':
      return 'Open settings';
    default:
      return 'Open dashboard';
  }
}
